import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, Package, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { track } from '../../lib/analytics';

export function DtpOrderConfirmation() {
  const { physicianId, orderId } = useDtpParams<{ physicianId: string; orderId: string }>();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const redirectStatus = searchParams.get('redirect_status');
  const paymentFailed = redirectStatus === 'failed';
  const paymentNeedsRetry = redirectStatus === 'requires_payment_method';
  const isError = paymentFailed || paymentNeedsRetry;

  // The patient is back from the payment form; whether it was paid is the
  // server's order_paid, this is the checkout UX completing.
  useEffect(() => {
    if (orderId) track('checkout_completed', { orderId, status: redirectStatus || 'none' });
  }, [orderId, redirectStatus]);

  useEffect(() => {
    if (!orderId) return;
    dtpApi.getOrder(orderId)
      .then(res => {
        if (res.success && res.data) {
          setOrder(res.data);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Payment failed or declined
  if (isError) {
    return (
      <div className="max-w-md mx-auto text-center mt-12">
        <div className={`rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6 ${
          paymentFailed ? 'bg-red-50' : 'bg-amber-50'
        }`}>
          {paymentFailed
            ? <XCircle className="w-10 h-10 text-red-600" />
            : <AlertTriangle className="w-10 h-10 text-amber-600" />
          }
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {paymentFailed ? 'Payment Declined' : 'Payment Not Completed'}
        </h1>
        <p className="text-gray-600 mb-6">
          {paymentFailed
            ? 'Your payment was declined. Please try a different card or payment method.'
            : 'Your payment could not be processed. Please try again with a different payment method.'}
        </p>

        {order && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 text-left">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
              <Package className="w-4 h-4" />
              Order #{order.orderNumber}
            </div>
            <div className="border-t border-gray-200 pt-2">
              <div className="flex justify-between text-base font-semibold">
                <span>Total</span>
                <span>${order.totalAmount?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <Link
            to={dtpPath(physicianId, `/checkout`)}
            className="flex items-center justify-center gap-2 w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Try Again
          </Link>
          <Link
            to={dtpPath(physicianId)}
            className="flex items-center justify-center w-full text-sm text-gray-600 hover:text-gray-900 py-2"
          >
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  // Payment succeeded
  return (
    <div className="max-w-md mx-auto text-center mt-12">
      <div className="bg-green-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>

      <h1 className="text-2xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
      <p className="text-gray-600 mb-6">
        Thank you for your order. We'll begin processing it shortly.
      </p>

      {order && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 text-left">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
            <Package className="w-4 h-4" />
            Order #{order.orderNumber}
          </div>
          <div className="divide-y divide-gray-100">
            {order.items?.map((item: any) => (
              <div key={item.productId} className="flex justify-between py-2">
                <span className="text-sm text-gray-700">{item.productName} x{item.quantity}</span>
                <span className="text-sm font-medium text-gray-900">${item.totalPrice?.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-200 pt-2 mt-2">
            <div className="flex justify-between text-base font-semibold">
              <span>Total</span>
              <span>${order.totalAmount?.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <Link
          to={dtpPath(physicianId, `/orders`)}
          className="flex items-center justify-center gap-2 w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          View My Orders
          <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to={dtpPath(physicianId)}
          className="flex items-center justify-center w-full text-sm text-gray-600 hover:text-gray-900 py-2"
        >
          Continue Shopping
        </Link>
      </div>
    </div>
  );
}
